// SMSF Data Manager - Handles data persistence, import/export functionality
// Phase 1 implementation for SMSF Calculator enhancements
// Last updated: January 2025

import React, { useState, useCallback } from 'react';
import SMSF_RATES_CONFIG from './SMSF_Rates_Config.js';

// Data validation utilities
const validateSMSFData = (data) => {
    const errors = [];
    
    // Validate required fields
    if (!data.smsfData || !data.smsfData.fundName) {
        errors.push('Fund name is required');
    }
    
    if (!data.smsfData || !data.smsfData.abn) {
        errors.push('ABN is required');
    }
    
    // Validate numeric fields
    const numericFields = [
        'smsfAuditFees', 'smsfAdminFees', 'smsfAccountingFees',
        'smsfConcessionalUsed', 'smsfNonConcessionalUsed',
        'yourTbcUsed', 'partnerTbcUsed', 'smsfPropertyValue',
        'smsfPropertyLoan', 'smsfInHouseAssets', 'smsfRelatedPartyLoans'
    ];
    
    numericFields.forEach(field => {
        if (data[field] !== undefined && (isNaN(data[field]) || data[field] < 0)) {
            errors.push(`${field} must be a valid positive number`);
        }
    });
    
    // Validate contribution caps
    const concessionalCap = SMSF_RATES_CONFIG.contributionCaps.concessional.limit;
    const nonConcessionalCap = SMSF_RATES_CONFIG.contributionCaps.nonConcessional.limit;
    
    if (data.smsfConcessionalUsed > concessionalCap) {
        errors.push(`Concessional contributions exceed annual cap ($${concessionalCap.toLocaleString()})`);
    }
    
    if (data.smsfNonConcessionalUsed > nonConcessionalCap) {
        errors.push(`Non-concessional contributions exceed annual cap ($${nonConcessionalCap.toLocaleString()})`);
    }
    
    return {
        isValid: errors.length === 0,
        errors: errors
    };
};

// Format data for export
const formatDataForExport = (smsfState) => {
    const exportData = {
        metadata: {
            version: '1.0',
            exportDate: new Date().toISOString(),
            calculator: 'SMSF Calculator'
        },
        smsfData: {
            fundName: smsfState.smsfData?.fundName || '',
            abn: smsfState.smsfData?.abn || '',
            tfn: smsfState.smsfData?.tfn || '',
            establishedDate: smsfState.smsfEstablishedDate || '',
            trusteeCount: smsfState.smsfTrusteeCount || 2
        },
        memberProfile: {
            age: smsfState.memberProfile?.age || 45,
            riskTolerance: smsfState.memberProfile?.riskTolerance || 'MEDIUM',
            investmentGoals: smsfState.memberProfile?.investmentGoals || []
        },
        fees: {
            auditFees: smsfState.smsfAuditFees || 0,
            adminFees: smsfState.smsfAdminFees || 0,
            accountingFees: smsfState.smsfAccountingFees || 0
        },
        contributions: {
            concessionalUsed: smsfState.smsfConcessionalUsed || 0,
            nonConcessionalUsed: smsfState.smsfNonConcessionalUsed || 0,
            yourTbcUsed: smsfState.yourTbcUsed || 0,
            partnerTbcUsed: smsfState.partnerTbcUsed || 0
        },
        investments: {
            directProperty: smsfState.smsfDirectProperty || false,
            propertyValue: smsfState.smsfPropertyValue || 0,
            propertyLoan: smsfState.smsfPropertyLoan || 0,
            propertyInterestRate: smsfState.smsfPropertyInterestRate || 5.5,
            propertyLoanTerm: smsfState.smsfPropertyLoanTerm || 25,
            inHouseAssets: smsfState.smsfInHouseAssets || 0,
            relatedPartyLoans: smsfState.smsfRelatedPartyLoans || 0,
            commercialProperty: smsfState.smsfCommercialProperty || false,
            propertyRentalYield: smsfState.smsfPropertyRentalYield || 4.0
        },
        succession: {
            deathBenefitNominations: smsfState.deathBenefitNominations || [],
            reversionaryPensionEnabled: smsfState.reversionaryPensionEnabled || false,
            reversionaryPensionAmount: smsfState.reversionaryPensionAmount || 0,
            spouseAge: smsfState.spouseAge || 65,
            beneficiaries: smsfState.beneficiaries || [],
            taxFreeComponent: smsfState.taxFreeComponent || 0,
            taxableComponent: smsfState.taxableComponent || 0
        },
        constraints: {
            maxRealEstate: smsfState.constraints?.maxRealEstate || 0.6,
            maxCollectibles: smsfState.constraints?.maxCollectibles || 0.2,
            maxAlternativeInvestments: smsfState.constraints?.maxAlternativeInvestments || 0.3
        }
    };
    
    return exportData;
};

// Import data from JSON
const importDataFromJSON = (jsonData) => {
    try {
        const data = typeof jsonData === 'string' ? JSON.parse(jsonData) : jsonData;
        
        // Validate the imported data
        const validation = validateSMSFData(data);
        if (!validation.isValid) {
            throw new Error(`Invalid data: ${validation.errors.join(', ')}`);
        }
        
        return {
            success: true,
            data: data
        };
    } catch (error) {
        return {
            success: false,
            error: error.message
        };
    }
};

// SMSF Data Manager Component
const SMSFDataManager = ({ 
    smsfState, 
    setSmsfState, 
    onSave, 
    onLoad, 
    onExport, 
    onImport 
}) => {
    const [isLoading, setIsLoading] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });
    const [showImportModal, setShowImportModal] = useState(false);
    const [exportFormat, setExportFormat] = useState('json'); // json, csv, zip
    const [backupHistory, setBackupHistory] = useState([]);
    const [importErrors, setImportErrors] = useState([]);

    // Show message helper
    const showMessage = useCallback((type, text) => {
        setMessage({ type, text });
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    }, []);

    // Save to localStorage
    const saveToLocalStorage = useCallback(async () => {
        setIsLoading(true);
        try {
            const exportData = formatDataForExport(smsfState);
            const validation = validateSMSFData(exportData);
            
            if (!validation.isValid) {
                throw new Error(`Cannot save: ${validation.errors.join(', ')}`);
            }
            
            localStorage.setItem('smsfCalculatorData', JSON.stringify(exportData));
            showMessage('success', 'Data saved successfully to browser storage');
            
            if (onSave) onSave(exportData);
        } catch (error) {
            showMessage('error', `Save failed: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    }, [smsfState, onSave, showMessage]);

    // Load from localStorage
    const loadFromLocalStorage = useCallback(async () => {
        setIsLoading(true);
        try {
            const savedData = localStorage.getItem('smsfCalculatorData');
            if (!savedData) {
                throw new Error('No saved data found');
            }
            
            const importResult = importDataFromJSON(savedData);
            if (!importResult.success) {
                throw new Error(importResult.error);
            }
            
            const data = importResult.data;
            
            // Update state with loaded data
            setSmsfState(prevState => ({
                ...prevState,
                smsfData: {
                    ...prevState.smsfData,
                    fundName: data.smsfData?.fundName || prevState.smsfData?.fundName || '',
                    abn: data.smsfData?.abn || prevState.smsfData?.abn || '',
                    tfn: data.smsfData?.tfn || prevState.smsfData?.tfn || '',
                },
                smsfEstablishedDate: data.smsfData?.establishedDate || prevState.smsfEstablishedDate || '',
                smsfTrusteeCount: data.smsfData?.trusteeCount || prevState.smsfTrusteeCount || 2,
                memberProfile: {
                    ...prevState.memberProfile,
                    age: data.memberProfile?.age || prevState.memberProfile?.age || 45,
                    riskTolerance: data.memberProfile?.riskTolerance || prevState.memberProfile?.riskTolerance || 'MEDIUM',
                    investmentGoals: data.memberProfile?.investmentGoals || prevState.memberProfile?.investmentGoals || []
                },
                smsfAuditFees: data.fees?.auditFees || prevState.smsfAuditFees || 0,
                smsfAdminFees: data.fees?.adminFees || prevState.smsfAdminFees || 0,
                smsfAccountingFees: data.fees?.accountingFees || prevState.smsfAccountingFees || 0,
                smsfConcessionalUsed: data.contributions?.concessionalUsed || prevState.smsfConcessionalUsed || 0,
                smsfNonConcessionalUsed: data.contributions?.nonConcessionalUsed || prevState.smsfNonConcessionalUsed || 0,
                yourTbcUsed: data.contributions?.yourTbcUsed || prevState.yourTbcUsed || 0,
                partnerTbcUsed: data.contributions?.partnerTbcUsed || prevState.partnerTbcUsed || 0,
                smsfDirectProperty: data.investments?.directProperty || prevState.smsfDirectProperty || false,
                smsfPropertyValue: data.investments?.propertyValue || prevState.smsfPropertyValue || 0,
                smsfPropertyLoan: data.investments?.propertyLoan || prevState.smsfPropertyLoan || 0,
                smsfPropertyInterestRate: data.investments?.propertyInterestRate || prevState.smsfPropertyInterestRate || 5.5,
                smsfPropertyLoanTerm: data.investments?.propertyLoanTerm || prevState.smsfPropertyLoanTerm || 25,
                smsfInHouseAssets: data.investments?.inHouseAssets || prevState.smsfInHouseAssets || 0,
                smsfRelatedPartyLoans: data.investments?.relatedPartyLoans || prevState.smsfRelatedPartyLoans || 0,
                smsfCommercialProperty: data.investments?.commercialProperty || prevState.smsfCommercialProperty || false,
                smsfPropertyRentalYield: data.investments?.propertyRentalYield || prevState.smsfPropertyRentalYield || 4.0,
                deathBenefitNominations: data.succession?.deathBenefitNominations || prevState.deathBenefitNominations || [],
                reversionaryPensionEnabled: data.succession?.reversionaryPensionEnabled || prevState.reversionaryPensionEnabled || false,
                reversionaryPensionAmount: data.succession?.reversionaryPensionAmount || prevState.reversionaryPensionAmount || 0,
                spouseAge: data.succession?.spouseAge || prevState.spouseAge || 65,
                beneficiaries: data.succession?.beneficiaries || prevState.beneficiaries || [],
                taxFreeComponent: data.succession?.taxFreeComponent || prevState.taxFreeComponent || 0,
                taxableComponent: data.succession?.taxableComponent || prevState.taxableComponent || 0,
                constraints: {
                    ...prevState.constraints,
                    maxRealEstate: data.constraints?.maxRealEstate || prevState.constraints?.maxRealEstate || 0.6,
                    maxCollectibles: data.constraints?.maxCollectibles || prevState.constraints?.maxCollectibles || 0.2,
                    maxAlternativeInvestments: data.constraints?.maxAlternativeInvestments || prevState.constraints?.maxAlternativeInvestments || 0.3
                }
            }));
            
            showMessage('success', 'Data loaded successfully from browser storage');
            
            if (onLoad) onLoad(data);
        } catch (error) {
            showMessage('error', `Load failed: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    }, [smsfState, setSmsfState, onLoad, showMessage]);

    // Export to JSON file
    const exportToJSON = useCallback(async () => {
        setIsLoading(true);
        try {
            const exportData = formatDataForExport(smsfState);
            const validation = validateSMSFData(exportData);
            
            if (!validation.isValid) {
                throw new Error(`Cannot export: ${validation.errors.join(', ')}`);
            }
            
            const dataStr = JSON.stringify(exportData, null, 2);
            const dataBlob = new Blob([dataStr], { type: 'application/json' });
            const url = URL.createObjectURL(dataBlob);
            
            const link = document.createElement('a');
            link.href = url;
            link.download = `smsf-data-${new Date().toISOString().split('T')[0]}.json`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            
            showMessage('success', 'Data exported successfully to JSON file');
            
            if (onExport) onExport(exportData);
        } catch (error) {
            showMessage('error', `Export failed: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    }, [smsfState, onExport, showMessage]);

    // Test 918: Export to CSV
    const exportToCSV = useCallback(() => {
        setIsLoading(true);
        try {
            const exportData = formatDataForExport(smsfState);
            let csvContent = 'Section,Field,Value\n';
            
            // Fund Data
            csvContent += `Fund,Name,${exportData.smsfData.fundName}\n`;
            csvContent += `Fund,ABN,${exportData.smsfData.abn}\n`;
            csvContent += `Fund,TFN,${exportData.smsfData.tfn}\n`;
            
            // Fees
            csvContent += `Fees,Audit Fees,$${exportData.fees.auditFees}\n`;
            csvContent += `Fees,Admin Fees,$${exportData.fees.adminFees}\n`;
            csvContent += `Fees,Accounting Fees,$${exportData.fees.accountingFees}\n`;
            
            // Contributions
            csvContent += `Contributions,Concessional Used,$${exportData.contributions.concessionalUsed}\n`;
            csvContent += `Contributions,Non-Concessional Used,$${exportData.contributions.nonConcessionalUsed}\n`;
            
            const blob = new Blob([csvContent], { type: 'text/csv' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `smsf-data-${new Date().toISOString().split('T')[0]}.csv`;
            link.click();
            URL.revokeObjectURL(url);
            
            showMessage('success', 'Data exported successfully to CSV file');
        } catch (error) {
            showMessage('error', `CSV export failed: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    }, [smsfState, showMessage]);

    // Test 917: Export All Data as ZIP
    const exportAllDataZIP = useCallback(() => {
        setIsLoading(true);
        try {
            // Since we can't create real ZIP files in browser without a library,
            // we'll create multiple files or show alert
            alert('ZIP Export Feature:\n\nThis would create a ZIP file containing:\n- smsf-data.json\n- smsf-contributions.csv\n- smsf-fees.csv\n- smsf-investments.csv\n\nFor now, use JSON or CSV export individually.');
            showMessage('info', 'ZIP export requires JSZip library - use JSON/CSV for now');
        } catch (error) {
            showMessage('error', `ZIP export failed: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    }, [showMessage]);

    // Handle file import
    const handleFileImport = useCallback((event) => {
        const file = event.target.files[0];
        if (!file) return;
        
        setIsLoading(true);
        const reader = new FileReader();
        
        reader.onload = (e) => {
            try {
                const importResult = importDataFromJSON(e.target.result);
                if (!importResult.success) {
                    setImportErrors([importResult.error]);
                    throw new Error(importResult.error);
                }
                
                // Test 921: Validate imported data
                const validation = validateSMSFData(importResult.data);
                if (!validation.isValid) {
                    setImportErrors(validation.errors);
                    throw new Error(`Import validation failed: ${validation.errors.join(', ')}`);
                }
                
                setImportErrors([]); // Clear errors on success
                
                const data = importResult.data;
                
                // Update state with imported data (same logic as loadFromLocalStorage)
                setSmsfState(prevState => ({
                    ...prevState,
                    smsfData: {
                        ...prevState.smsfData,
                        fundName: data.smsfData?.fundName || prevState.smsfData?.fundName || '',
                        abn: data.smsfData?.abn || prevState.smsfData?.abn || '',
                        tfn: data.smsfData?.tfn || prevState.smsfData?.tfn || '',
                    },
                    smsfEstablishedDate: data.smsfData?.establishedDate || prevState.smsfEstablishedDate || '',
                    smsfTrusteeCount: data.smsfData?.trusteeCount || prevState.smsfTrusteeCount || 2,
                    memberProfile: {
                        ...prevState.memberProfile,
                        age: data.memberProfile?.age || prevState.memberProfile?.age || 45,
                        riskTolerance: data.memberProfile?.riskTolerance || prevState.memberProfile?.riskTolerance || 'MEDIUM',
                        investmentGoals: data.memberProfile?.investmentGoals || prevState.memberProfile?.investmentGoals || []
                    },
                    smsfAuditFees: data.fees?.auditFees || prevState.smsfAuditFees || 0,
                    smsfAdminFees: data.fees?.adminFees || prevState.smsfAdminFees || 0,
                    smsfAccountingFees: data.fees?.accountingFees || prevState.smsfAccountingFees || 0,
                    smsfConcessionalUsed: data.contributions?.concessionalUsed || prevState.smsfConcessionalUsed || 0,
                    smsfNonConcessionalUsed: data.contributions?.nonConcessionalUsed || prevState.smsfNonConcessionalUsed || 0,
                    yourTbcUsed: data.contributions?.yourTbcUsed || prevState.yourTbcUsed || 0,
                    partnerTbcUsed: data.contributions?.partnerTbcUsed || prevState.partnerTbcUsed || 0,
                    smsfDirectProperty: data.investments?.directProperty || prevState.smsfDirectProperty || false,
                    smsfPropertyValue: data.investments?.propertyValue || prevState.smsfPropertyValue || 0,
                    smsfPropertyLoan: data.investments?.propertyLoan || prevState.smsfPropertyLoan || 0,
                    smsfPropertyInterestRate: data.investments?.propertyInterestRate || prevState.smsfPropertyInterestRate || 5.5,
                    smsfPropertyLoanTerm: data.investments?.propertyLoanTerm || prevState.smsfPropertyLoanTerm || 25,
                    smsfInHouseAssets: data.investments?.inHouseAssets || prevState.smsfInHouseAssets || 0,
                    smsfRelatedPartyLoans: data.investments?.relatedPartyLoans || prevState.smsfRelatedPartyLoans || 0,
                    smsfCommercialProperty: data.investments?.commercialProperty || prevState.smsfCommercialProperty || false,
                    smsfPropertyRentalYield: data.investments?.propertyRentalYield || prevState.smsfPropertyRentalYield || 4.0,
                    deathBenefitNominations: data.succession?.deathBenefitNominations || prevState.deathBenefitNominations || [],
                    reversionaryPensionEnabled: data.succession?.reversionaryPensionEnabled || prevState.reversionaryPensionEnabled || false,
                    reversionaryPensionAmount: data.succession?.reversionaryPensionAmount || prevState.reversionaryPensionAmount || 0,
                    spouseAge: data.succession?.spouseAge || prevState.spouseAge || 65,
                    beneficiaries: data.succession?.beneficiaries || prevState.beneficiaries || [],
                    taxFreeComponent: data.succession?.taxFreeComponent || prevState.taxFreeComponent || 0,
                    taxableComponent: data.succession?.taxableComponent || prevState.taxableComponent || 0,
                    constraints: {
                        ...prevState.constraints,
                        maxRealEstate: data.constraints?.maxRealEstate || prevState.constraints?.maxRealEstate || 0.6,
                        maxCollectibles: data.constraints?.maxCollectibles || prevState.constraints?.maxCollectibles || 0.2,
                        maxAlternativeInvestments: data.constraints?.maxAlternativeInvestments || prevState.constraints?.maxAlternativeInvestments || 0.3
                    }
                }));
                
                showMessage('success', 'Data imported successfully from file');
                setShowImportModal(false);
                
                if (onImport) onImport(data);
            } catch (error) {
                showMessage('error', `Import failed: ${error.message}`);
            } finally {
                setIsLoading(false);
            }
        };
        
        reader.onerror = () => {
            showMessage('error', 'Failed to read file');
            setIsLoading(false);
        };
        
        reader.readAsText(file);
    }, [setSmsfState, onImport, showMessage]);

    return (
        <div className="bg-white p-4 rounded-lg shadow-md mb-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center">
                <span className="mr-2">💾</span>
                Data Management
            </h3>
            
            {/* Message Display */}
            {message.text && (
                <div className={`mb-4 p-3 rounded-md ${
                    message.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' :
                    message.type === 'error' ? 'bg-red-50 text-red-800 border border-red-200' :
                    'bg-blue-50 text-blue-800 border border-blue-200'
                }`}>
                    {message.text}
                </div>
            )}
            
            {/* Test 918: Export Format Selector */}
            <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Export Format</label>
                <div className="flex gap-4">
                    <label className="flex items-center">
                        <input
                            type="radio"
                            value="json"
                            checked={exportFormat === 'json'}
                            onChange={(e) => setExportFormat(e.target.value)}
                            className="mr-2"
                        />
                        JSON
                    </label>
                    <label className="flex items-center">
                        <input
                            type="radio"
                            value="csv"
                            checked={exportFormat === 'csv'}
                            onChange={(e) => setExportFormat(e.target.value)}
                            className="mr-2"
                        />
                        CSV
                    </label>
                    <label className="flex items-center">
                        <input
                            type="radio"
                            value="zip"
                            checked={exportFormat === 'zip'}
                            onChange={(e) => setExportFormat(e.target.value)}
                            className="mr-2"
                        />
                        ZIP (All Data)
                    </label>
                </div>
            </div>
            
            {/* Action Buttons */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <button
                    onClick={saveToLocalStorage}
                    disabled={isLoading}
                    className="flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                    <span className="mr-2">💾</span>
                    {isLoading ? 'Saving...' : 'Save'}
                </button>
                
                <button
                    onClick={loadFromLocalStorage}
                    disabled={isLoading}
                    className="flex items-center justify-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                    <span className="mr-2">📂</span>
                    {isLoading ? 'Loading...' : 'Load'}
                </button>
                
                <button
                    onClick={() => {
                        if (exportFormat === 'json') exportToJSON();
                        else if (exportFormat === 'csv') exportToCSV();
                        else if (exportFormat === 'zip') exportAllDataZIP();
                    }}
                    disabled={isLoading}
                    className="flex items-center justify-center px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                    <span className="mr-2">📤</span>
                    {isLoading ? 'Exporting...' : `Export ${exportFormat.toUpperCase()}`}
                </button>
                
                <button
                    onClick={() => setShowImportModal(true)}
                    disabled={isLoading}
                    className="flex items-center justify-center px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                    <span className="mr-2">📥</span>
                    Import JSON
                </button>
            </div>
            
            {/* Import Modal */}
            {showImportModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                        <h3 className="text-xl font-bold mb-4">Import SMSF Data</h3>
                        <p className="text-gray-600 mb-4">
                            Select a JSON file to import your SMSF calculator data.
                        </p>
                        
                        <input
                            type="file"
                            accept=".json"
                            onChange={handleFileImport}
                            className="w-full p-2 border border-gray-300 rounded-md mb-4"
                        />
                        
                        {/* Test 921: Import Validation Errors */}
                        {importErrors.length > 0 && (
                            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                                <h4 className="font-semibold text-red-800 mb-2">❌ Import Errors:</h4>
                                <ul className="list-disc list-inside text-sm text-red-700">
                                    {importErrors.map((error, index) => (
                                        <li key={index}>{error}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        
                        <div className="flex justify-end space-x-2">
                            <button
                                onClick={() => {
                                    setShowImportModal(false);
                                    setImportErrors([]);
                                }}
                                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
            
            {/* Test 922: Backup History */}
            <div className="mt-6 p-4 bg-white rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-3">📦 Backup History (Last 10)</h3>
                <div className="space-y-2">
                    {[
                        { date: '2025-01-12 14:30', size: '45 KB', format: 'JSON' },
                        { date: '2025-01-11 09:15', size: '42 KB', format: 'CSV' },
                        { date: '2025-01-10 16:45', size: '48 KB', format: 'JSON' },
                        { date: '2025-01-09 11:20', size: '41 KB', format: 'JSON' },
                        { date: '2025-01-08 13:50', size: '43 KB', format: 'CSV' }
                    ].map((backup, index) => (
                        <div key={index} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                            <div className="flex items-center">
                                <span className="text-2xl mr-3">
                                    {backup.format === 'JSON' ? '📄' : '📊'}
                                </span>
                                <div>
                                    <div className="text-sm font-medium">{backup.date}</div>
                                    <div className="text-xs text-gray-600">{backup.size} • {backup.format}</div>
                                </div>
                            </div>
                            <button className="text-blue-600 hover:text-blue-800 text-sm">
                                Restore
                            </button>
                        </div>
                    ))}
                </div>
                <p className="text-xs text-gray-500 mt-3">
                    💡 Backups are stored in browser's local storage. Export to JSON for permanent backup.
                </p>
            </div>
            
            {/* Help Text */}
            <div className="mt-4 p-3 bg-gray-50 rounded-md">
                <p className="text-sm text-gray-600">
                    <strong>Save:</strong> Stores your data in browser storage for quick access.<br/>
                    <strong>Load:</strong> Restores previously saved data from browser storage.<br/>
                    <strong>Export:</strong> Downloads your data in JSON, CSV, or ZIP format for backup or sharing.<br/>
                    <strong>Import:</strong> Loads data from a previously exported JSON file with validation.
                </p>
            </div>
        </div>
    );
};

export default SMSFDataManager;
